import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { DarkMode, LightMode, Language } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "@/context/ThemeContext";
import { useLanguageToggle } from "@/hooks/useLanguageToggle";

type Variant = "toolbar" | "login" | "appBar";

interface LanguageThemeControlsProps {
  /** `toolbar`: public header. `login`: login page. `appBar`: authenticated top bar (inherit colors). */
  variant?: Variant;
}

/**
 * Shared language switch + light/dark toggle (Login, public Header, app TopBar).
 */
export default function LanguageThemeControls({
  variant = "toolbar",
}: LanguageThemeControlsProps) {
  const { t } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const { languageCode, languageButtonLabel, toggleLanguage } = useLanguageToggle();

  const isLogin = variant === "login";
  const isAppBar = variant === "appBar";
  const isToolbar = variant === "toolbar";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isLogin ? 1 : 0.5,
      }}
    >
      <Tooltip title={languageButtonLabel}>
        <Button
          size="small"
          startIcon={<Language />}
          onClick={toggleLanguage}
          color={isLogin || isAppBar ? "inherit" : undefined}
          sx={{
            minWidth: "auto",
            ...(isToolbar ? { color: "text.secondary" } : {}),
          }}
        >
          {languageCode.toUpperCase()}
        </Button>
      </Tooltip>
      <Tooltip title={mode === "light" ? t("theme.dark") : t("theme.light")}>
        <IconButton
          onClick={toggleTheme}
          size="small"
          color={isAppBar ? "inherit" : undefined}
          sx={isToolbar ? { color: "text.secondary" } : undefined}
        >
          {mode === "light" ? <DarkMode /> : <LightMode />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
